import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'uniqueCategories', label: 'Unique Categories', icon: '📂', defaultVisible: true },
  { id: 'uniqueTypes', label: 'Unique Types', icon: '🔧', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byCategory', label: 'By Category', icon: '📂', defaultVisible: true, defaultType: 'bar' },
  { id: 'byType', label: 'By Type', icon: '🔧', defaultVisible: true, defaultType: 'bar' },
  { id: 'priceRanges', label: 'Price Ranges', icon: '💵', defaultVisible: true, defaultType: 'bar' },
];

/**
 * MaterialIndexAnalyticsDashboard - Analytics Dashboard for Material Index Log
 * Displays index trends, category breakdown, and pricing patterns
 */
const MaterialIndexAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('materialIndexAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('material_index_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return { totalRecords: 0, uniqueCategories: 0, uniqueTypes: 0, byCategory: [], byType: [], priceRanges: [] };
    }

    const totalRecords = data.length;
    const uniqueCategories = new Set(data.map(d => d.category).filter(Boolean)).size;
    const uniqueTypes = new Set(data.map(d => d.type_ottav).filter(Boolean)).size;

    // By Category
    const catMap = {};
    data.forEach(d => {
      const cat = d.category || 'Unspecified';
      if (!catMap[cat]) catMap[cat] = { name: cat, count: 0 };
      catMap[cat].count++;
    });
    const byCategory = Object.values(catMap).sort((a, b) => b.count - a.count).slice(0, 15);

    // By Type
    const typeMap = {};
    data.forEach(d => {
      const type = d.type_ottav || 'Unspecified';
      if (!typeMap[type]) typeMap[type] = { name: type, count: 0 };
      typeMap[type].count++;
    });
    const byType = Object.values(typeMap).sort((a, b) => b.count - a.count).slice(0, 15);

    return { totalRecords, uniqueCategories, uniqueTypes, byCategory, byType };
  }, [data]);

  const colors = { primary: '#6366f1', secondary: '#8b5cf6', success: '#10b981', warning: '#f59e0b', dark: '#1e293b', chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'] };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title }) => {
    const chartData = data.slice(0, 12);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '120px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[labelKey]}</div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: colors.chartColors[idx % colors.chartColors.length], borderRadius: '4px' }} />
                </div>
                <div style={{ width: '50px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>{value}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📑 Material Index Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Index trends, category breakdown, and pricing patterns</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{ padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>✕ Close</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <MetricCard title="Total Items" value={analytics.totalRecords} icon="📋" color={colors.primary} />
                <MetricCard title="Categories" value={analytics.uniqueCategories} icon="📂" color={colors.success} />
                <MetricCard title="Types" value={analytics.uniqueTypes} icon="🏷️" color={colors.warning} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
                <BarChart data={analytics.byCategory} valueKey="count" labelKey="name" title="Items by Category" />
                <BarChart data={analytics.byType} valueKey="count" labelKey="name" title="Items by Type" />
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8' }}>
          {analytics.totalRecords} items | {analytics.uniqueCategories} categories | {analytics.uniqueTypes} types
        </div>
      </div>
    </div>
  );
};

export default MaterialIndexAnalyticsDashboard;

